import { Component, Input, OnInit } from '@angular/core';
import { SignAssociation } from 'src/app/components/stock/sign-associations/edit-association/sign-association';
import { SignAssociationAsset } from 'src/app/components/stock/sign-associations/edit-association/sign-association-asset';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import { BoardTypeChoices } from 'src/app/model/product/board/board-part';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import IPBoardPart from 'src/app/model/product/ip-board/ip-board-part';
import { IP_BOARD_PART_TYPE_CHOICES } from 'src/app/model/product/ip-board/ip-board-part-type';
import { Project } from 'src/app/model/project/project';
import { SignAsset } from 'src/app/model/sign/sign-asset';
import {
  ProductAssetsChangedEvent,
  ProjectDisplayObservableService,
} from 'src/app/services/project-display-observable.service';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import { ClientIPBoardAndPlacementHelper } from '../../../client-project-form/base-project/client-ip-board-form/helper/client-ip-board-and-placement-helper';
import { ClientSignSelectorHelper } from '../../../client-project-form/base-project/client-ip-board-form/client-sign-selector/client-sign-selector-helper';
import { KeysOfType } from 'src/app/helpers/utility-functions';

@Component({
  selector: 'ip-board-design-preview',
  templateUrl: './ip-board-design-preview.component.html',
  styleUrls: ['./ip-board-design-preview.component.scss'],
})
export class IPBoardDesignPreviewComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit
{
  @Input()
  ipBoard: IPBoard;
  @Input()
  project: Project;
  @Input()
  withFrame: boolean = false;
  @Input()
  showCustomSignExample: boolean = false;

  boardPartSignExampleStyleLeft: any;
  boardPartSignExampleStyleRight: any;

  recommendedCollaborativeOption: boolean = false;

  constructor(
    private projectDisplayObservableService: ProjectDisplayObservableService
  ) {
    super();
  }

  ngOnInit(): void {
    this.setBoardPartSignExamples();

    const setSignExampleObservable = this.createObservableUntilDestroy(
      this.projectDisplayObservableService.getSignSelectedSubscribable(),
      this.projectDisplayObservableService.getIPBoardConfigurationChangedSubscribable()
    );

    this.addSubscription(setSignExampleObservable, (event: any) => {
      if (this.evaluateRecalculateExamples(event)) {
        this.setBoardPartSignExamples();
      }
    });

    this.addSubscription(
      this.projectDisplayObservableService.getProductAssetsChangedSubscribable(),
      (event: ProductAssetsChangedEvent) => {
        if (
          event.productId === this.ipBoard.id &&
          event.formSection === 'extraDesign'
        ) {
          this.setBoardPartSignExamples();
        }
      }
    );
  }

  private evaluateRecalculateExamples(event: any) {
    return (
      event == null ||
      (event.ipBoardPart != null &&
        ((this.ipBoard.boardPart != null &&
          this.ipBoard.boardPart.id === event.ipBoardPart.id) ||
          this.ipBoard.boardPart === event.ipBoardPart))
    );
  }

  private setBoardPartSignExamples(): void {
    if (!this.project.collaborative) {
      this.setIndividualProjectExamples();
    } else {
      this.setCollaborativeProjectExample();
    }
  }

  private setIndividualProjectExamples(): void {
    if (
      this.ipBoard.boardPart != null &&
      (this.ipBoard.boardPart.sign != null || this.showCustomSignExample)
    ) {
      this.setBoardPartSignExampleLeft(
        this.ipBoard.boardPart,
        'boardPartSignExampleStyleLeft'
      );
      this.setBoardPartSignExampleRight();
    } else {
      this.boardPartSignExampleStyleLeft = null;
      this.boardPartSignExampleStyleRight = null;
    }
  }

  private setBoardPartSignExampleLeft(
    boardPart: IPBoardPart,
    exampleStyleKey: KeysOfType<IPBoardDesignPreviewComponent, any>
  ): void {
    if (this.mustShowEmptyLeftExample(boardPart)) {
      this[exampleStyleKey] = null;
      return;
    }
    const exampleCandidate = this.isExampleCandidate(boardPart.titleAdjustment)
      ? boardPart.titleAdjustment
      : this.isExampleCandidate(boardPart.phoneNumberAdjustment)
      ? boardPart.phoneNumberAdjustment
      : this.isExampleCandidate(boardPart.sticker)
      ? boardPart.sticker
      : null;
    if (exampleCandidate != null) {
      const displayExample =
        this.getSignAssociationDisplayExample(exampleCandidate);
      if (displayExample != null) {
        this[exampleStyleKey] =
          ClientSignSelectorHelper.getBackgroundImageStyle(
            `/api/assets/${displayExample.asset.uuid}`
          );
      } else {
        this[exampleStyleKey] = null;
      }
      return;
    } else if (this.showCustomSignExample && boardPart.sign == null) {
      this[exampleStyleKey] = ProductHelper.productHasAssetOfFormSection(
        this.ipBoard,
        'extraDesign'
      )
        ? null
        : this.getCustomSignPreviewStyle();
      return;
    } else if (boardPart.sign != null) {
      const displayExample = this.getSignDisplayExample(
        boardPart.sign.examples
      );
      if (displayExample != null) {
        this[exampleStyleKey] =
          ClientSignSelectorHelper.getBackgroundImageStyle(
            `/api/assets/${displayExample.asset.uuid}`
          );
      }
      return;
    }
    this[exampleStyleKey] = null;
  }

  private mustShowEmptyLeftExample(boardPart: IPBoardPart): boolean {
    return (
      this.ipBoard.draftDesign ||
      (!this.showCustomSignExample && boardPart.sign == null) ||
      ((!this.showCustomSignExample || boardPart.sign != null) &&
        this.leftNonStockAdjustment(boardPart))
    );
  }

  private leftNonStockAdjustment(boardPart: IPBoardPart): boolean {
    return (
      !StringHelper.isEmpty(boardPart.otherTitle) ||
      !StringHelper.isEmpty(boardPart.otherPhoneNumber) ||
      !StringHelper.isEmpty(boardPart.otherSticker)
    );
  }

  private setBoardPartSignExampleRight(): void {
    if (this.ipBoard.leftRightBoard) {
      this.setBoardPartSignExampleLeft(
        this.ipBoard.otherSideBoardPart,
        'boardPartSignExampleStyleRight'
      );
    } else {
      this.setSingleBoardPartSignExampleRight();
    }
  }

  private setSingleBoardPartSignExampleRight(): void {
    if (this.mustShowEmptyRightExample()) {
      this.boardPartSignExampleStyleRight = null;
      return;
    }
    const exampleCandidate = this.isExampleCandidate(
      this.ipBoard.boardPart.otherTitleAdjustment
    )
      ? this.ipBoard.boardPart.otherTitleAdjustment
      : this.isExampleCandidate(
          this.ipBoard.boardPart.otherPhoneNumberAdjustment
        )
      ? this.ipBoard.boardPart.otherPhoneNumberAdjustment
      : this.isExampleCandidate(this.ipBoard.boardPart.sticker)
      ? this.ipBoard.boardPart.sticker
      : null;
    if (exampleCandidate != null) {
      const displayExample =
        this.getSignAssociationDisplayExample(exampleCandidate);
      if (displayExample != null) {
        this.boardPartSignExampleStyleRight =
          ClientSignSelectorHelper.getBackgroundImageStyle(
            `/api/assets/${displayExample.asset.uuid}`
          );
      } else {
        this.boardPartSignExampleStyleRight = null;
      }
      return;
    } else if (
      this.showCustomSignExample &&
      this.ipBoard.boardPart.sign == null
    ) {
      this.boardPartSignExampleStyleRight =
        ProductHelper.productHasAssetOfFormSection(this.ipBoard, 'extraDesign')
          ? null
          : this.getCustomSignPreviewStyle();
      return;
    } else if (this.ipBoard.boardPart.sign != null) {
      const displayExample = this.getSignDisplayExample(
        this.ipBoard.boardPart.sign.examples
      );
      if (displayExample != null) {
        this.boardPartSignExampleStyleRight =
          ClientSignSelectorHelper.getBackgroundImageStyle(
            `/api/assets/${displayExample.asset.uuid}`
          );
      }
      return;
    }
    this.boardPartSignExampleStyleRight = null;
  }

  private mustShowEmptyRightExample(): boolean {
    return (
      this.ipBoard.draftDesign ||
      !this.rightSideCanDiffer() ||
      (!this.showCustomSignExample && this.ipBoard.boardPart.sign == null) ||
      ((!this.showCustomSignExample || this.ipBoard.boardPart.sign != null) &&
        this.rightNonStockAdjustment())
    );
  }

  private rightNonStockAdjustment(): boolean {
    return (
      !StringHelper.isEmpty(this.ipBoard.boardPart.otherSideOtherTitle) ||
      !StringHelper.isEmpty(this.ipBoard.boardPart.otherSideOtherPhoneNumber) ||
      !StringHelper.isEmpty(this.ipBoard.boardPart.otherSticker)
    );
  }

  private isExampleCandidate(signAssociation: SignAssociation): boolean {
    return signAssociation != null && signAssociation.graphicsComplete;
  }

  private rightSideCanDiffer() {
    return (
      (this.ipBoard.leftRightBoard ||
        (this.ipBoard.boardPart.boardType != null &&
          this.ipBoard.boardPart.boardType.ordinal ===
            BoardTypeChoices.V.ordinal)) &&
      this.ipBoard.boardPart.sign != null &&
      (ProductHelper.doTitleSidesDiffer(this.ipBoard.boardPart) ||
        ProductHelper.doPhoneNumberSidesDiffer(this.ipBoard.boardPart))
    );
  }

  private getCustomSignPreviewStyle(): any {
    return this.ipBoard.boardPart.otherIPBoardPartType != null &&
      this.ipBoard.boardPart.otherIPBoardPartType.ordinal ===
        IP_BOARD_PART_TYPE_CHOICES.ADHESIVE_BOARD.ordinal
      ? ClientSignSelectorHelper.getBackgroundImageStyle(
          `${ClientIPBoardAndPlacementHelper.ASSETS_URL_PREFIX}customkleefbord_vexip.png`
        )
      : ClientSignSelectorHelper.getBackgroundImageStyle(
          `${ClientIPBoardAndPlacementHelper.ASSETS_URL_PREFIX}custombord.png`
        );
  }

  private getSignDisplayExample(examples: SignAsset[]): SignAsset {
    return CollectionHelper.isPopulated(examples)
      ? examples.find((e) => e != null && !e.archived)
      : null;
  }

  private getSignAssociationDisplayExample(
    signAssociation: SignAssociation
  ): SignAssociationAsset {
    return CollectionHelper.isPopulated(signAssociation.examples)
      ? signAssociation.examples.find((e) => e != null && !e.archived)
      : null;
  }

  private setCollaborativeProjectExample(): void {
    this.boardPartSignExampleStyleRight = null;
    if (this.ipBoard.boardPart == null) {
      this.boardPartSignExampleStyleLeft = null;
      this.recommendedCollaborativeOption = false;
      return;
    }

    const extraDesignAsset = CollectionHelper.isPopulated(
      this.ipBoard.productAssets
    )
      ? this.ipBoard.productAssets.find(
          (pa) => !pa.archived && pa.formSection === 'extraDesign'
        )
      : null;

    if (extraDesignAsset != null) {
      this.boardPartSignExampleStyleLeft =
        ClientSignSelectorHelper.getBackgroundImageStyle(
          `/api/assets/${extraDesignAsset.asset.uuid}`
        );
      this.recommendedCollaborativeOption = false;
      return;
    }

    if (
      this.ipBoard.boardPart.otherWidth === 100 &&
      this.ipBoard.boardPart.otherHeight === 140 &&
      this.ipBoard.boardPart.otherIPBoardPartType != null &&
      this.ipBoard.boardPart.otherIPBoardPartType.ordinal ===
        IP_BOARD_PART_TYPE_CHOICES.IP_100_140.ordinal
    ) {
      this.boardPartSignExampleStyleLeft =
        ClientSignSelectorHelper.getBackgroundImageStyle(
          `${ClientIPBoardAndPlacementHelper.ASSETS_URL_PREFIX}gezamenlijk-aanbevolen.png`
        );
      this.recommendedCollaborativeOption = true;
    } else {
      this.boardPartSignExampleStyleLeft =
        ClientSignSelectorHelper.getBackgroundImageStyle(
          `${ClientIPBoardAndPlacementHelper.ASSETS_URL_PREFIX}gezamenlijk-ander.png`
        );
      this.recommendedCollaborativeOption = false;
    }
  }
}
